import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import { FileText, Receipt, Percent, Calculator, Briefcase, ArrowRight, ShieldCheck, Clock, Award } from 'lucide-react';

const ICONS = {
  'file-text': FileText,
  'receipt': Receipt,
  'percent': Percent,
  'calculator': Calculator,
  'briefcase': Briefcase,
};

const TaxServicesPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    // Capture utm
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach((k) => {
      const v = params.get(k);
      if (v) sessionStorage.setItem(k, v);
    });
    axios.get(`${API}/tax-services/catalog`).then((r) => setCatalog(r.data.services || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }} data-testid="tax-services-page">
      <Navbar />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 50%, #EFF6FF 100%)', padding: '5rem 1.5rem 3rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.4rem 0.9rem', background: '#FF6B35', color: 'white', borderRadius: 999, fontSize: '0.8rem', fontWeight: 600, marginBottom: '1.25rem' }}>
            <Award size={14} /> TAX SERVICES BY ARTHIK
          </div>
          <h1 style={{ fontSize: 'clamp(2.25rem, 5vw, 3.5rem)', color: '#0F172A', margin: 0, lineHeight: 1.1, fontWeight: 800 }}>
            Tax Filing, Done by <span style={{ color: '#FF6B35' }}>Expert CAs</span>
          </h1>
          <p style={{ fontSize: '1.15rem', color: '#475569', marginTop: '1.25rem', maxWidth: 720, lineHeight: 1.6 }}>
            ITR, GST, TDS, Tax Planning & Business Registration — fully online, transparent pricing, 100% accurate filings. Trusted by salaried professionals & small businesses across India.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.75rem', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/tax-services/assistant')} style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white', border: 'none', padding: '0.85rem 1.5rem', borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem', display: 'inline-flex', alignItems: 'center', gap: 8, boxShadow: '0 6px 16px rgba(16,185,129,0.35)' }} data-testid="tax-assistant-cta">
              ✨ Ask AI Tax Assistant
            </button>
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0F172A', fontWeight: 600, fontSize: '0.9rem' }}>
              <ShieldCheck size={18} style={{ color: '#10B981' }} /> Qualified CAs
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0F172A', fontWeight: 600, fontSize: '0.9rem' }}>
              <Clock size={18} style={{ color: '#FF6B35' }} /> Filed in 24-48 hrs
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0F172A', fontWeight: 600, fontSize: '0.9rem' }}>
              <Award size={18} style={{ color: '#3B82F6' }} /> ₹499 onwards
            </div>
          </div>
        </div>
      </section>

      {/* Service grid */}
      <section style={{ padding: '3rem 1.5rem 5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', color: '#0F172A', marginBottom: '0.5rem' }}>Pick your tax service</h2>
          <p style={{ color: '#475569', marginBottom: '2.5rem' }}>Transparent pricing. No hidden fees. Expert CA on every case.</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '1.5rem' }}>
            {catalog.map((svc) => {
              const Icon = ICONS[svc.icon] || FileText;
              const startingPrice = Math.min(...(svc.plans || []).map(p => p.price));
              return (
                <div
                  key={svc.slug}
                  className="service-card"
                  data-testid={`tax-service-card-${svc.slug}`}
                  onClick={() => navigate(`/tax-services/${svc.slug}`)}
                  style={{ cursor: 'pointer', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}
                >
                  <div style={{ width: 52, height: 52, borderRadius: 14, background: `${svc.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={26} style={{ color: svc.color }} />
                  </div>
                  <h3 style={{ fontSize: '1.25rem', color: '#0F172A', margin: 0, fontWeight: 700 }}>{svc.title}</h3>
                  <p style={{ color: '#64748B', fontSize: '0.95rem', margin: 0, lineHeight: 1.5, flex: 1 }}>{svc.tagline}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: '#94A3B8', textTransform: 'uppercase', fontWeight: 700 }}>Starting at</div>
                      <div style={{ fontSize: '1.5rem', color: svc.color, fontWeight: 800 }}>₹{startingPrice.toLocaleString('en-IN')}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: svc.color, fontWeight: 600, fontSize: '0.9rem' }}>
                      View plans <ArrowRight size={16} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section style={{ background: '#0F172A', padding: '3rem 1.5rem', color: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '2rem', textAlign: 'center' }}>
          <div>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#FF6B35' }}>10,000+</div>
            <div style={{ color: '#CBD5E1', fontSize: '0.95rem' }}>Returns filed</div>
          </div>
          <div>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#FF6B35' }}>24-48h</div>
            <div style={{ color: '#CBD5E1', fontSize: '0.95rem' }}>Average filing time</div>
          </div>
          <div>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#FF6B35' }}>100%</div>
            <div style={{ color: '#CBD5E1', fontSize: '0.95rem' }}>Accuracy guarantee</div>
          </div>
          <div>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#FF6B35' }}>4.8★</div>
            <div style={{ color: '#CBD5E1', fontSize: '0.95rem' }}>Customer rating</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TaxServicesPage;
